/**
 * "Would a call to our number actually reach this browser?"
 *
 * Everything else on the Inbound card is a stored setting, so the card can show
 * it without asking anyone. This one lives in the Twilio console: a phone number
 * has to be routed to the TwiML App, and the app to our own webhook, both by
 * hand. Nothing in the CRM writes those two fields, and nothing until now read
 * them - so an installation where the switch says "answering" and no number is
 * wired up looked exactly like a working one, and the symptom was silence.
 *
 * Checked on load rather than behind a button, and only once the credentials are
 * there to check with. The operator who opens this page after "I turned it on
 * and it never rings" should not have to know which button asks the question.
 */
import { useCallback, useEffect, useState } from "react";
import { AlertCircle, CheckCircle2, PhoneCall, RefreshCw } from "lucide-react";
import { useT } from "../../../../i18n/I18nProvider";
import { testInboundBinding, type VoiceBindingResult } from "../../../../lib/calls";
import { Btn } from "../../../ui";
import { FieldGroup, Note } from "../panel/fields";
import { bindingNotes, boundNumberLabel } from "./bindingNotes";

export function InboundBinding({
  enabled,
  configured,
  voiceWebhookUrl,
}: {
  /** The saved state of the switch, not the unsaved one: the check reads what
   *  the server is running on, and so should the tone of its verdict. */
  enabled: boolean;
  /** No credentials, no check - `checkVoiceBinding` would only say so again. */
  configured: boolean;
  /** The address the TwiML App is supposed to hold, from the settings payload. */
  voiceWebhookUrl: string;
}) {
  const t = useT();
  const [result, setResult] = useState<VoiceBindingResult | null>(null);
  const [busy, setBusy] = useState(false);

  const run = useCallback(async () => {
    setBusy(true);
    try {
      setResult(await testInboundBinding());
    } catch {
      // The endpoint failing is itself an answer, and the operator gets the same
      // "could not check" wording as a Twilio that did not respond.
      setResult({
        ok: false,
        appUrlMatches: false,
        appVoiceUrl: "",
        appMethodMatches: false,
        numbers: [],
        truncated: false,
        error: "twilio_upstream",
      });
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    if (!configured) return;
    void run();
  }, [configured, run]);

  if (!configured) return null;

  const notes = result ? bindingNotes(result, { enabled, expectedUrl: voiceWebhookUrl }) : [];
  const wiredUp = result?.ok === true;

  return (
    <FieldGroup title={t("settings.inbound.binding.title")}>
      <Note tone="mut">{t("settings.inbound.binding.about")}</Note>

      {result === null ? (
        <Note tone="mut">{t("settings.inbound.binding.checking")}</Note>
      ) : (
        <>
          {wiredUp ? (
            <Note tone="ok" icon={<CheckCircle2 className="mt-0.5 size-4 shrink-0" />} live>
              {t("settings.inbound.binding.ok", {
                numbers: result.numbers.map(boundNumberLabel).join(", "),
              })}
            </Note>
          ) : null}
          {notes.map((note) => (
            <Note
              key={note.key}
              tone={note.tone}
              icon={
                note.tone === "bad" ? <AlertCircle className="mt-0.5 size-4 shrink-0" /> : undefined
              }
              live
            >
              {t(`settings.inbound.binding.${note.key}`, note.vars)}
            </Note>
          ))}
        </>
      )}

      <div>
        <Btn onClick={run} disabled={busy}>
          {busy ? <RefreshCw className="size-3.5 animate-spin" /> : <PhoneCall className="size-3.5" />}
          {busy ? t("settings.inbound.binding.checking") : t("settings.inbound.binding.recheck")}
        </Btn>
      </div>
    </FieldGroup>
  );
}
